/**
 * OnboardingTour — a lightweight guided walkthrough that highlights key areas
 * of the pharmacy ERP for first-time staff. Uses a floating tooltip that points
 * to real DOM elements via data-testid selectors. No external library needed.
 *
 * Stored in localStorage: `dutilyne_onboarding_done`.
 * Can be re-triggered from the help menu.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../components/ui/button';
import { X, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

const STORAGE_KEY = 'dutilyne_onboarding_done';

const STEPS = [
  {
    target: '[data-testid="sidebar-nav"]',
    title: 'Navigation',
    body: 'Use the sidebar to move between modules — Sales, Inventory, Clinical, Finance, and more. Each section expands to show its pages.',
    placement: 'right',
  },
  {
    target: 'a[href="/pos"]',
    title: 'Point of Sale',
    body: 'Start selling here. Search products, scan barcodes, add items to the cart, and complete transactions with multiple payment methods.',
    placement: 'right',
  },
  {
    target: 'a[href="/inventory"]',
    title: 'Inventory',
    body: 'Manage your pharmacy\'s products, stock levels, batches, and expiry dates. Use bulk import to add products from CSV.',
    placement: 'right',
  },
  {
    target: '[data-testid="notification-bell"]',
    title: 'Notifications',
    body: 'This bell shows alerts — low stock warnings, prescription uploads, expiring products, and more. Critical items appear in red.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="user-menu"]',
    title: 'Your Account',
    body: 'Access your profile, switch branches, change settings, or log out from here.',
    placement: 'bottom',
  },
];

const getTargetRect = (step) => {
  const selectors = step.target.split(',').map((s) => s.trim());
  for (const sel of selectors) {
    const el = document.querySelector(sel);
    if (el) return el.getBoundingClientRect();
  }
  return null;
};

const Spotlight = ({ rect }) => {
  if (!rect) return null;
  const pad = 6;
  return (
    <div className="fixed inset-0 z-[9998] pointer-events-none" style={{ transition: 'opacity 200ms' }}>
      <svg className="absolute inset-0 w-full h-full">
        <defs>
          <mask id="onboarding-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={rect.left - pad} y={rect.top - pad}
              width={rect.width + pad * 2} height={rect.height + pad * 2}
              rx="10" fill="black"
            />
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#onboarding-mask)" />
      </svg>
    </div>
  );
};

const Tooltip = ({ step, stepIndex, total, rect, onNext, onPrev, onSkip }) => {
  const style = {};
  if (!rect) {
    // Target not found — float the tooltip in the center so the user can skip/advance
    style.left = '50%';
    style.top = '40%';
    style.transform = 'translate(-50%, -50%)';
  } else {
    const placement = step.placement || 'bottom';
    if (placement === 'right') {
      style.left = rect.right + 16;
      style.top = rect.top;
    } else {
      style.left = Math.max(16, rect.left + rect.width / 2 - 160);
      style.top = rect.bottom + 12;
    }
    style.left = Math.min(style.left, window.innerWidth - 340);
    style.top = Math.min(style.top, window.innerHeight - 200);
  }

  return (
    <div
      className="fixed z-[9999] bg-white rounded-xl shadow-2xl border border-border p-4 w-[300px]"
      style={style}
      data-testid="onboarding-tooltip"
    >
      <button className="absolute top-2 right-2 text-muted-foreground hover:text-foreground" onClick={onSkip} data-testid="onboarding-skip">
        <X className="h-4 w-4" />
      </button>
      <p className="text-xs font-bold text-primary uppercase tracking-wide mb-1">
        Step {stepIndex + 1} of {total}
      </p>
      <h3 className="font-semibold text-sm mb-1">{step.title}</h3>
      <p className="text-xs text-muted-foreground leading-relaxed">{step.body}</p>
      <div className="flex items-center justify-between mt-3">
        <Button variant="ghost" size="sm" disabled={stepIndex === 0} onClick={onPrev} className="text-xs gap-1" data-testid="onboarding-prev">
          <ChevronLeft className="h-3 w-3" /> Back
        </Button>
        <Button size="sm" onClick={onNext} className="text-xs gap-1" data-testid="onboarding-next">
          {stepIndex === total - 1 ? 'Finish' : 'Next'} <ChevronRight className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};

export const OnboardingTour = () => {
  const { user, setAuthUser } = useAuth();
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const rafRef = useRef(null);

  // Auto-show once per ACCOUNT (server flag), not once per device/browser.
  useEffect(() => {
    if (!user) return;
    const done = user.onboarding_done || localStorage.getItem(STORAGE_KEY);
    // Chain owners finish branch setup first; the tour waits for a later visit.
    if (!done && !window.location.search.includes('setup=chain')) {
      // Wait for the layout and for any blocking dialog (opening float, shift, alerts) to close.
      let t;
      const tryStart = () => {
        if (document.querySelector('[role="dialog"], [role="alertdialog"]')) { t = setTimeout(tryStart, 1500); return; }
        setActive(true);
      };
      t = setTimeout(tryStart, 2000);
      return () => clearTimeout(t);
    }
  }, [user]);

  // Track target element position
  useEffect(() => {
    if (!active) return;
    const update = () => {
      const r = getTargetRect(STEPS[step]);
      setRect(r);
      rafRef.current = requestAnimationFrame(update);
    };
    update();
    return () => cancelAnimationFrame(rafRef.current);
  }, [active, step]);

  const finish = useCallback(() => {
    setActive(false);
    setStep(0);
    localStorage.setItem(STORAGE_KEY, '1');
    api.post('/auth/onboarding-complete').catch(() => {});
    if (user) setAuthUser({ ...user, onboarding_done: true });
  }, [user, setAuthUser]);

  const next = () => {
    if (step >= STEPS.length - 1) { finish(); return; }
    setStep((s) => s + 1);
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  if (!active) return null;

  return createPortal(
    <>
      <Spotlight rect={rect} />
      <Tooltip step={STEPS[step]} stepIndex={step} total={STEPS.length} rect={rect} onNext={next} onPrev={prev} onSkip={finish} />
    </>,
    document.body,
  );
};

/** Button to re-trigger the tour from a help menu or settings page. */
export const StartTourButton = ({ className }) => {
  const handleStart = () => {
    localStorage.removeItem(STORAGE_KEY);
    api.post('/auth/onboarding-reset').catch(() => {}).finally(() => window.location.reload());
  };
  return (
    <Button variant="outline" size="sm" className={`gap-1.5 ${className || ''}`} onClick={handleStart} data-testid="start-tour-btn">
      <Sparkles className="h-3.5 w-3.5" /> Replay Onboarding Tour
    </Button>
  );
};
